import { Link } from '@/components/ui/link';
import { AppStoreBadges } from '@/components/app-store-badges';
import { Title } from '@/components/ui/layout';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Smartphone } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useLanguage } from '@/i18n/language-context';
import { usePageMetadata } from '@/i18n/use-page-metadata';
import { SiteShell } from '@/components/site-shell';
import { env } from '@/config/env';
import { useAnalytics } from '@/lib/use-analytics';

export const AppFallbackPage = (): JSX.Element => {
  const track = useAnalytics();
  const { t } = useLanguage();
  usePageMetadata();
  const [searchParams] = useSearchParams();
  const params = useParams<{ organizationId: string; projectId: string }>();
  const projectId = params.projectId ?? searchParams.get('project');
  const orgId = params.organizationId ?? searchParams.get('org');
  const fund = searchParams.get('fund');
  const slug = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  const valid = (value: string | null | undefined): value is string =>
    value !== null && value !== undefined && value.length <= 200 && slug.test(value);
  const nativeHref =
    valid(projectId) && fund === null
      ? params.organizationId && valid(orgId)
        ? `mygiving://app/organizations/${orgId}/projects/${projectId}`
        : `mygiving://app/p/${projectId}`
      : (projectId === null || projectId === undefined) && valid(orgId)
        ? fund === 'tithe' || fund === 'general-fund'
          ? `mygiving://app/organizations/${orgId}/projects/${fund}`
          : fund === null
            ? `mygiving://app/o/${orgId}`
            : null
        : null;
  const hasStoreLink = env.iosAppStoreUrl !== null || env.androidAppStoreUrl !== null;

  return (
    <SiteShell>
      <main id="main-content" tabIndex={-1} className="center-page">
        <div className="info-panel ui-stack">
          <span className="info-panel__icon">
            <Smartphone aria-hidden />
          </span>
          <p className="eyebrow">{t.mobileApp}</p>
          <Title level={1}>{hasStoreLink ? t.downloadNamed : t.appPreparing}</Title>
          <p>{hasStoreLink ? t.appDescription : t.appComingSoon}</p>
          <AppStoreBadges placement="app_fallback" />
          <div className="button-row">
            {nativeHref !== null && (
              <Link
                appearance="primary"

                href={nativeHref}
                onClick={() =>
                  track('Public App Open Clicked', {
                    destination: 'native_app',
                    placement: 'app_fallback',
                    ...(fund === 'tithe' || fund === 'general-fund' ? { fund_type: fund } : {}),
                  })
                }
              >
                {t.openApp}
              </Link>
            )}
            {(projectId !== null && projectId !== undefined) ||
            (orgId !== null && orgId !== undefined) ? (
              <Button
                variant="secondary"

                type="button"
                onClick={() => window.history.back()}
              >
                <ArrowLeft size={16} aria-hidden />{' '}
                {projectId !== null ? t.backProject : t.backOrganization}
              </Button>
            ) : (
              <Link appearance="secondary" to="/">
                <ArrowLeft size={16} aria-hidden /> {t.home}
              </Link>
            )}
          </div>
        </div>
      </main>
    </SiteShell>
  );
};
